// Firebase error handling with user-friendly messages
// Converts Firebase error codes into human-readable messages for better UX

import type { AuthError } from 'firebase/auth';
import type { FirestoreError } from 'firebase/firestore';

// User-friendly error messages
export interface UserFriendlyError {
  code: string;
  message: string;
  category: 'auth' | 'firestore' | 'storage' | 'network' | 'unknown';
  severity: 'info' | 'warning' | 'error';
}

// Firebase Auth error mappings
const AUTH_ERROR_MESSAGES: Record<string, Omit<UserFriendlyError, 'code'>> = {
  // Sign In Errors
  'auth/user-not-found': {
    message:
      'No account found with this email address. Please check your email or create a new account.',
    category: 'auth',
    severity: 'error',
  },
  'auth/wrong-password': {
    message: 'Incorrect password. Please try again or reset your password.',
    category: 'auth',
    severity: 'error',
  },
  // What a project with email-enumeration protection ON actually returns for
  // BOTH a wrong password and an address with no account — Firebase makes the
  // two deliberately indistinguishable, so the sentence has to name both. The
  // two legacy codes above still fire where that protection is off, and their
  // more specific wording is better there, so they stay.
  //
  // Without this entry the fallthrough was `DEFAULT_ERRORS.auth`,
  // "Authentication failed. Please try again." — which points at nothing the
  // user can act on, and in particular never suggests the EMAIL might be
  // wrong. That is the whole failure in #1947: someone arriving from a
  // relying party finds the address prefilled from its `login_hint`, types the
  // password for their differently-addressed account, and is told the system
  // failed. The recovery they reach for is "create an account", which makes a
  // duplicate that can never be merged.
  //
  // `severity: 'error'` because a wrong credential is not a
  // retry-the-same-thing condition: `isTemporaryError` below returns true for
  // any `severity === 'warning'`. Nothing depends on that today — that helper
  // has no production caller — so this is the correct value waiting for one,
  // not a live behaviour.
  'auth/invalid-credential': {
    message:
      'Incorrect email address or password. Please check both and try again.',
    category: 'auth',
    severity: 'error',
  },
  'auth/invalid-email': {
    message: 'Please enter a valid email address.',
    category: 'auth',
    severity: 'error',
  },
  'auth/user-disabled': {
    message:
      'This account has been disabled. Please contact support for assistance.',
    category: 'auth',
    severity: 'error',
  },
  'auth/too-many-requests': {
    message: 'Too many failed attempts. Please wait a moment and try again.',
    category: 'auth',
    severity: 'warning',
  },

  // Sign Up Errors
  'auth/email-already-in-use': {
    message:
      'An account with this email already exists. Please sign in instead.',
    category: 'auth',
    severity: 'error',
  },
  'auth/weak-password': {
    message:
      'Password is too weak. Please use at least 8 characters with a mix of letters and numbers.',
    category: 'auth',
    severity: 'error',
  },
  'auth/operation-not-allowed': {
    message: 'Account creation is currently disabled. Please contact support.',
    category: 'auth',
    severity: 'error',
  },

  // Email Verification Errors
  'auth/expired-action-code': {
    message: 'This verification link has expired. Please request a new one.',
    category: 'auth',
    severity: 'warning',
  },
  'auth/invalid-action-code': {
    message: 'This verification link is invalid. Please request a new one.',
    category: 'auth',
    severity: 'error',
  },

  // General Auth Errors
  'auth/network-request-failed': {
    message: 'Network error. Please check your connection and try again.',
    category: 'network',
    severity: 'error',
  },
  'auth/internal-error': {
    message: 'Something went wrong on our end. Please try again in a moment.',
    category: 'auth',
    severity: 'error',
  },
};

// Firestore error mappings
const FIRESTORE_ERROR_MESSAGES: Record<
  string,
  Omit<UserFriendlyError, 'code'>
> = {
  'permission-denied': {
    message: 'You do not have permission to perform this action.',
    category: 'firestore',
    severity: 'error',
  },
  'not-found': {
    message: 'The requested data could not be found.',
    category: 'firestore',
    severity: 'error',
  },
  'already-exists': {
    message: 'This item already exists.',
    category: 'firestore',
    severity: 'error',
  },
  'resource-exhausted': {
    message: 'Too many requests. Please wait a moment and try again.',
    category: 'firestore',
    severity: 'warning',
  },
  'failed-precondition': {
    message: 'Operation cannot be performed due to current conditions.',
    category: 'firestore',
    severity: 'error',
  },
  unavailable: {
    message:
      'Service is temporarily unavailable. Please try again in a moment.',
    category: 'firestore',
    severity: 'warning',
  },
  'deadline-exceeded': {
    message: 'Request timed out. Please try again.',
    category: 'network',
    severity: 'error',
  },
};

// Default error messages
const DEFAULT_ERRORS = {
  auth: {
    message: 'Authentication failed. Please try again.',
    category: 'auth' as const,
    severity: 'error' as const,
  },
  firestore: {
    message: 'Database operation failed. Please try again.',
    category: 'firestore' as const,
    severity: 'error' as const,
  },
  storage: {
    message: 'File operation failed. Please try again.',
    category: 'storage' as const,
    severity: 'error' as const,
  },
  network: {
    message: 'Network error. Please check your connection and try again.',
    category: 'network' as const,
    severity: 'error' as const,
  },
  unknown: {
    message: 'An unexpected error occurred. Please try again.',
    category: 'unknown' as const,
    severity: 'error' as const,
  },
};

// Handle Firebase Auth errors
export function handleAuthError(error: AuthError): UserFriendlyError {
  const errorMapping = AUTH_ERROR_MESSAGES[error.code];

  if (errorMapping) {
    return {
      code: error.code,
      ...errorMapping,
    };
  }

  // Log unknown error for debugging
  console.warn('Unknown auth error:', error.code, error.message);

  return {
    code: error.code,
    ...DEFAULT_ERRORS.auth,
  };
}

// Handle Firestore errors
export function handleFirestoreError(error: FirestoreError): UserFriendlyError {
  const errorMapping = FIRESTORE_ERROR_MESSAGES[error.code];

  if (errorMapping) {
    return {
      code: error.code,
      ...errorMapping,
    };
  }

  // Log unknown error for debugging
  console.warn('Unknown Firestore error:', error.code, error.message);

  return {
    code: error.code,
    ...DEFAULT_ERRORS.firestore,
  };
}

// Generic Firebase error handler
export function handleFirebaseError(error: unknown): UserFriendlyError {
  // Check if it's an Auth error
  if (error && typeof error === 'object' && 'code' in error) {
    const firebaseError = error as { code: string; message: string };

    // Auth errors start with 'auth/'
    if (firebaseError.code.startsWith('auth/')) {
      return handleAuthError(firebaseError as AuthError);
    }

    // Firestore errors (other Firebase errors)
    if (firebaseError.code in FIRESTORE_ERROR_MESSAGES) {
      return handleFirestoreError(firebaseError as FirestoreError);
    }
  }

  // Network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return {
      code: 'network/fetch-failed',
      ...DEFAULT_ERRORS.network,
    };
  }

  // Log unknown error for debugging
  console.error('Unknown Firebase error:', error);

  return {
    code: 'unknown',
    ...DEFAULT_ERRORS.unknown,
  };
}

// Extract user-friendly message from any error
export function getErrorMessage(error: unknown): string {
  // Plain Error objects (without a Firebase .code property) should use their
  // message directly — e.g. errors thrown by our own API call wrappers such
  // as the backend signup endpoint returning "Signup failed".
  if (error instanceof Error && !('code' in error)) {
    return error.message;
  }

  const userFriendlyError = handleFirebaseError(error);
  return userFriendlyError.message;
}

// Check if error requires user action (e.g., email verification)
export function requiresUserAction(error: UserFriendlyError): boolean {
  const actionRequiredCodes = [
    'auth/email-not-verified',
    'auth/expired-action-code',
    'auth/invalid-action-code',
    'auth/too-many-requests',
  ];

  return actionRequiredCodes.includes(error.code);
}

// Check if error is temporary (user should retry)
export function isTemporaryError(error: UserFriendlyError): boolean {
  const temporaryCodes = [
    'auth/network-request-failed',
    'auth/too-many-requests',
    'resource-exhausted',
    'unavailable',
    'deadline-exceeded',
    'network/fetch-failed',
  ];

  return temporaryCodes.includes(error.code) || error.severity === 'warning';
}

/**
 * Firebase error codes a person re-entering their password can actually do
 * something about.
 *
 * Deliberately separate from `handleAuthError` above: this returns the one
 * sentence that goes under a password field, not a `UserFriendlyError` with a
 * severity and a suggested action. It also still covers `auth/user-mismatch`,
 * which that map does not carry.
 *
 * It stays separate now that the map DOES carry `auth/invalid-credential`
 * (#1947), because the two surfaces are asking different questions. Here the
 * address is settled — the person is re-entering the password for the account
 * they are already signed in as — so "That password is not correct" is exactly
 * right, and the map's "Incorrect email address or password" would invite them
 * to go looking for a mistake they cannot have made.
 *
 * Shared by the OIDC step-up (`OidcContinuePage`) and account deletion
 * (`DeleteAccountSection`). Both ask the same question for different reasons,
 * and a wrong password has to read the same either way.
 */
export function reauthErrorMessage(error: unknown): string {
  const code =
    typeof error === 'object' && error !== null && 'code' in error
      ? String((error as { code: unknown }).code)
      : '';

  switch (code) {
    // `auth/invalid-credential` is what a modern project returns for a wrong
    // password; `auth/wrong-password` is the older code and still appears where
    // email-enumeration protection is off. Both mean the same thing to the
    // person typing, so both must be handled — matching only one leaves a
    // wrong password showing "something went wrong", with no hint to retry.
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
      return 'That password is not correct. Please try again.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a few minutes and try again.';
    case 'auth/user-mismatch':
      return 'Those credentials belong to a different account.';
    case 'auth/network-request-failed':
      return 'Could not reach the sign-in service. Check your connection and try again.';
    default:
      return 'Could not confirm your password. Please try again.';
  }
}
